// Super-admin — PFP User Analytics dashboard.
//
// Two panes:
//   1. Overview counters (single call to /admin/pfp-analytics/overview).
//   2. User table with search + filter + engagement columns
//      (paginated via /admin/pfp-analytics/users).
//
// Deliberately simple — no charts, no BI complexity. Marketing / retention
// signals only: last login, last activity, play-idea count, therapist link.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { PFPEngagementPanel } from '../../components/admin/PFPEngagementPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active (30d)' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'self_paying', label: 'Self-paying' },
  { key: 'school_sponsored', label: 'School-sponsored' },
  { key: 'trial', label: 'Trial' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().split('T')[0]; } catch { return String(iso).slice(0, 10); }
}

export default function AdminPFPAnalytics() {
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const loadOverview = async () => {
    try {
      const r = await axios.get(`${API}/admin/pfp-analytics/overview`, headers);
      setOverview(r.data);
    } catch (e) { /* silent */ }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (search) params.search = search;
      if (filter) params.filter = filter;
      const r = await axios.get(`${API}/admin/pfp-analytics/users`, { ...headers, params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { loadOverview(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, [filter, offset]);

  const counts = overview?.counts || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-pfp-analytics-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-serif text-stone-900">PFP user analytics</h1>
        <Button variant="outline" onClick={() => { loadOverview(); loadUsers(); }} data-testid="pfp-analytics-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pfp-analytics-overview">
        {[
          ['Total PFP', counts.total],
          ['Active (30d)', counts.active_last_30_days],
          ['Inactive', counts.inactive_last_30_days],
          ['Self-paying', counts.self_paying],
          ['School-sponsored', counts.school_sponsored],
          ['Trial', counts.trial],
          ['Active subs', counts.active_subscriptions],
          ['Cancelled/inactive', counts.cancelled_or_inactive_subscriptions],
          ['With therapist', counts.with_therapist],
          ['No therapist', counts.without_therapist],
        ].map(([label, val]) => (
          <Card key={label} className="p-3">
            <p className="text-xs text-stone-500">{label}</p>
            <p className="text-2xl font-serif text-stone-900 mt-1">{val ?? '—'}</p>
          </Card>
        ))}
      </div>

      {/* Filters + search */}
      <PFPEngagementPanel />

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => { setOffset(0); setFilter(f.key); }}
              className={`px-3 py-1.5 text-xs rounded-full border ${
                filter === f.key
                  ? 'bg-[#7C6F9C] text-white border-[#7C6F9C]'
                  : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50'
              }`}
              data-testid={`pfp-analytics-filter-${f.key || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); loadUsers(); } }}
              placeholder="Search name or email…"
              className="pl-9"
              data-testid="pfp-analytics-search"
            />
          </div>
          <Button onClick={() => { setOffset(0); loadUsers(); }}>Search</Button>
        </div>
      </Card>

      {/* Users table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-xs text-stone-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Parent</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-left px-3 py-2">Sub</th>
                <th className="text-left px-3 py-2">Therapist</th>
                <th className="text-left px-3 py-2">Children</th>
                <th className="text-left px-3 py-2">Last login</th>
                <th className="text-left px-3 py-2">Last activity</th>
                <th className="text-right px-3 py-2">Play ideas</th>
                <th className="text-right px-3 py-2">Feedback</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-6 text-stone-500">
                  <Loader2 className="w-4 h-4 animate-spin inline-block" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-stone-500">No parents match.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`pfp-analytics-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-stone-900">{r.name || '(no name)'}</div>
                    <div className="text-xs text-stone-500">{r.email}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.plan || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.subscription_status || '—'}
                    {r.sponsoring_practice_id && <div className="text-[10px] text-emerald-700">School</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.therapist ? r.therapist.name || r.therapist.email : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(r.children || []).slice(0, 2).join(', ') || '—'}
                    {r.children?.length > 2 && ` +${r.children.length - 2}`}
                  </td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.last_login_at)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.last_activity_at)}</td>
                  <td className="px-3 py-2 text-right">{r.engagement?.play_ideas || 0}</td>
                  <td className="px-3 py-2 text-right">{r.engagement?.feedback || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between p-3 border-t border-stone-100 text-xs">
          <span className="text-stone-500">
            Showing {rows.length ? offset + 1 : 0}–{offset + rows.length} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              data-testid="pfp-analytics-prev"
            >Prev</Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + rows.length >= total}
              onClick={() => setOffset(offset + limit)}
              data-testid="pfp-analytics-next"
            >Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
